import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Palette, Image as ImageIcon, TextT, Rows, Warning } from "@phosphor-icons/react";

// Landing Page Theming — Design System & CRO Priority 1.
// Presentational only: this component NEVER mutates the theme unless the
// user explicitly touches a control. A page with all-blank fields is treated
// server-side as `theme = null` → renders the untouched defaults.

const FONT_CHOICES = [
  { value: "",                    label: "— Default (untouched) —" },
  { value: "system",              label: "System — Urbanist / Arapey (current)" },
  { value: "modern-sans",         label: "Modern Sans — Playfair + Inter" },
  { value: "editorial",           label: "Editorial — Cormorant + Inter" },
  { value: "friendly-humanist",   label: "Friendly Humanist — Merriweather + Nunito" },
  { value: "clean-swiss",         label: "Clean Swiss — DM Serif + DM Sans" },
];
const BUTTON_STYLES = [
  { value: "",         label: "— Default —" },
  { value: "rounded",  label: "Rounded" },
  { value: "square",   label: "Square" },
  { value: "pill",     label: "Pill" },
];
const RADII = [
  { value: "",       label: "— Default —" },
  { value: "none",   label: "None (0px)" },
  { value: "small",  label: "Small (4px)" },
  { value: "medium", label: "Medium (8px)" },
  { value: "large",  label: "Large (16px)" },
];
const SPACING = [
  { value: "",              label: "— Default —" },
  { value: "compact",       label: "Compact" },
  { value: "comfortable",   label: "Comfortable" },
  { value: "spacious",      label: "Spacious" },
];

const EMPTY = {
  primary_color: "", secondary_color: "", accent_color: "", background_color: "",
  logo_url: "", font_choice: "", button_style: "", border_radius: "", spacing_density: "",
};

export default function LandingPageTheme({ value, onChange }) {
  // Local mirror so uncontrolled color inputs feel responsive.
  const theme = value && typeof value === "object" ? { ...EMPTY, ...value } : { ...EMPTY };
  const isConfigured = Object.values(theme).some(v => v);
  const patch = (k, v) => onChange({ ...theme, [k]: v });
  const clearAll = () => onChange(null);

  return (
    <div className="space-y-5" data-testid="lp-theme-editor" data-configured={isConfigured ? "true" : "false"}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Palette weight="duotone" className="h-3 w-3" /> Theme
          </div>
          <p className="mt-2 text-sm text-ink-secondary max-w-2xl">
            Optional overrides applied across every section. Leave anything blank to keep the built-in default.
            An unconfigured page renders exactly as it did before this feature existed.
          </p>
        </div>
        {isConfigured && (
          <button type="button"
                  data-testid="lp-theme-reset"
                  onClick={clearAll}
                  className="rounded-full border border-white/10 bg-graphite-950 px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary hover:bg-white/5">
            Reset to defaults
          </button>
        )}
      </div>

      {/* Colors */}
      <section className="rounded-md border border-white/10 bg-graphite-900 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-secondary">Colors</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorField label="Primary"     testid="lp-theme-primary"    value={theme.primary_color}    onChange={v => patch("primary_color", v)} />
          <ColorField label="Secondary"   testid="lp-theme-secondary"  value={theme.secondary_color}  onChange={v => patch("secondary_color", v)} />
          <ColorField label="Accent"      testid="lp-theme-accent"     value={theme.accent_color}     onChange={v => patch("accent_color", v)} />
          <ColorField label="Background"  testid="lp-theme-background" value={theme.background_color} onChange={v => patch("background_color", v)} />
        </div>
      </section>

      {/* Logo */}
      <section className="rounded-md border border-white/10 bg-graphite-900 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          <ImageIcon weight="duotone" className="h-3 w-3" /> Logo URL
        </div>
        <div className="flex items-start gap-3 flex-wrap">
          <Input
            data-testid="lp-theme-logo-url"
            value={theme.logo_url}
            onChange={e => patch("logo_url", e.target.value)}
            placeholder="https://example.com/logo.svg"
            maxLength={1000}
            className="flex-1 min-w-[240px] bg-graphite-950 border-white/10"
          />
          <LogoPreview url={theme.logo_url} />
        </div>
        <p className="text-[11px] text-ink-secondary/70">Direct URL to a hosted logo (SVG or PNG recommended). File upload is a separate deferred capability.</p>
      </section>

      {/* Typography & layout */}
      <section className="rounded-md border border-white/10 bg-graphite-900 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-secondary flex items-center gap-1.5">
          <TextT weight="duotone" className="h-3 w-3" /> Typography &amp; layout
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SelectField label="Font pairing"    testid="lp-theme-font"    value={theme.font_choice}     onChange={v => patch("font_choice", v)}     options={FONT_CHOICES} />
          <SelectField label="Button style"    testid="lp-theme-button"  value={theme.button_style}    onChange={v => patch("button_style", v)}    options={BUTTON_STYLES} />
          <SelectField label="Border radius"   testid="lp-theme-radius"  value={theme.border_radius}   onChange={v => patch("border_radius", v)}   options={RADII} />
          <SelectField label="Spacing density" testid="lp-theme-spacing" value={theme.spacing_density} onChange={v => patch("spacing_density", v)} options={SPACING} />
        </div>
      </section>

      <div className="rounded-md border border-white/10 bg-graphite-950/40 p-3 text-[11px] text-ink-secondary flex items-start gap-2">
        <Rows weight="duotone" className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Changes apply to <span className="text-ink-primary">all 8 section types</span> on the public page via CSS variables — a single override layer, no per-section special-casing.</span>
      </div>
    </div>
  );
}

function ColorField({ label, testid, value, onChange }) {
  const hex = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value || "") ? value : "";
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          data-testid={`${testid}-picker`}
          value={hex || "#000000"}
          onChange={e => onChange(e.target.value)}
          className="h-9 w-12 rounded-md bg-graphite-950 border border-white/10 cursor-pointer p-1"
        />
        <input
          data-testid={`${testid}-hex`}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder="#RRGGBB"
          maxLength={9}
          className="flex-1 h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm font-mono text-ink-primary focus:outline-none focus:border-white/30"
        />
      </div>
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">{label}</label>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function LogoPreview({ url }) {
  const [state, setState] = useState("idle"); // idle | ok | error
  useEffect(() => {
    if (!url) { setState("idle"); return; }
    let cancelled = false;
    const img = new Image();
    img.onload  = () => { if (!cancelled) setState("ok"); };
    img.onerror = () => { if (!cancelled) setState("error"); };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);

  if (!url) {
    return (
      <div data-testid="lp-theme-logo-preview-empty"
           className="w-16 h-16 rounded-md border border-dashed border-white/15 grid place-items-center text-[10px] uppercase tracking-widest text-ink-secondary/70">
        No logo
      </div>
    );
  }
  if (state === "error") {
    return (
      <div data-testid="lp-theme-logo-preview-error"
           className="w-16 h-16 rounded-md border border-amber-400/40 bg-amber-400/5 grid place-items-center text-amber-200">
        <Warning weight="duotone" className="h-5 w-5" />
      </div>
    );
  }
  return (
    <img data-testid="lp-theme-logo-preview-ok"
         src={url} alt=""
         className="w-16 h-16 object-contain rounded-md border border-white/10 bg-graphite-950 p-1" />
  );
}
